export const APP_LANGUAGES = ['en', 'ar'] as const;

export type AppLanguage = (typeof APP_LANGUAGES)[number];
export type AppDirection = 'ltr' | 'rtl';

export const DEFAULT_LANGUAGE: AppLanguage = 'en';
export const LANGUAGE_STORAGE_KEY = 'satrpdf.language';

export function isAppLanguage(value: unknown): value is AppLanguage {
  return typeof value === 'string' && APP_LANGUAGES.some((language) => language === value);
}

export function languageFromTag(tag: string | null | undefined): AppLanguage | null {
  if (!tag) {
    return null;
  }

  const baseLanguage = tag.trim().toLowerCase().split(/[-_]/u)[0];
  return isAppLanguage(baseLanguage) ? baseLanguage : null;
}

export function getLanguageDirection(language: AppLanguage): AppDirection {
  return language === 'ar' ? 'rtl' : 'ltr';
}

export function getStoredLanguage(): AppLanguage | null {
  if (typeof window === 'undefined') {
    return null;
  }

  try {
    const storedLanguage = window.localStorage.getItem(LANGUAGE_STORAGE_KEY);
    return isAppLanguage(storedLanguage) ? storedLanguage : null;
  } catch {
    return null;
  }
}

export function hasStoredLanguage(): boolean {
  return getStoredLanguage() !== null;
}

export function storeLanguage(language: AppLanguage): boolean {
  if (typeof window === 'undefined') {
    return false;
  }

  try {
    window.localStorage.setItem(LANGUAGE_STORAGE_KEY, language);
    return true;
  } catch {
    return false;
  }
}

export function clearStoredLanguage(): boolean {
  if (typeof window === 'undefined') {
    return false;
  }

  try {
    window.localStorage.removeItem(LANGUAGE_STORAGE_KEY);
    return true;
  } catch {
    return false;
  }
}

/**
 * The browser locale is deliberately used only to choose initial dialog focus.
 * It must never count as a first-run language selection.
 */
export function getBrowserLanguageFocus(): AppLanguage {
  if (typeof navigator === 'undefined') {
    return DEFAULT_LANGUAGE;
  }

  const primaryLanguage = navigator.languages[0] ?? navigator.language;
  return languageFromTag(primaryLanguage) ?? DEFAULT_LANGUAGE;
}

export function getInitialAppLanguage(): AppLanguage {
  return getStoredLanguage() ?? DEFAULT_LANGUAGE;
}

export function applyDocumentLanguage(language: AppLanguage): void {
  if (typeof document === 'undefined') {
    return;
  }

  const root = document.documentElement;
  root.lang = language;
  root.dir = getLanguageDirection(language);
  root.dataset.language = language;
}
